import { useNavigate } from '@tanstack/react-router';
import { PiLifebuoy, PiPlus } from 'react-icons/pi';
import type { EmployeeListResponse } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { CardSkeleton } from 'src/modules/shared/components/atoms/cards/card-skeleton';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PageHeader } from 'src/modules/shared/components/molecules/page-header';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { useEmployees } from 'src/modules/shared/hooks/api/automations/employees/employees';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import EmployeeCard from '../../molecules/employees/employee-card';
import {
  employeeSearchSchema,
  newEmployeeSearchSchema,
} from 'src/modules/automations/schemas/employees/employees.schema';
import { executionsSearchSchema } from 'src/modules/automations/schemas/employees/executions.schema';
import { Route as EmployeesEmployeeIdRoute } from 'src/routes/employees/$employeeId.index';
import { Route as EmployeesEmployeeIdExecutionsRoute } from 'src/routes/employees/$employeeId/executions/index';
import { Route as EmployeesNewRoute } from 'src/routes/employees/new';
import { Route as EmployeeAssistancesRoute } from 'src/routes/employees/assistances';
import { assistancesSearchSchema } from 'src/modules/automations/schemas/employees/assistances.schema';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import { EMPLOYEE_ASSISTANCE_ROUTE_PERMISSIONS } from 'src/modules/automations/constants/employee-assistance.constants';

const EmployeesLoadingState: React.FC = () => (
  <div className="mx-auto w-full max-w-5xl space-y-3">
    {Array.from({ length: 6 }, (_, index) => (
      <CardSkeleton
        key={index}
        className="min-h-56 border border-neutral-100"
        header={{ avatar: true, title: true }}
        content={{ lines: 4, widths: ['w-full', 'w-5/6', 'w-2/3', 'w-full'] }}
      />
    ))}
  </div>
);

/** AI Agent index route composed from shared page and card primitives. */
export const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { employees, loading, error, refetch } = useEmployees();
  const {
    hasPermission: canReadAssistances,
    loading: assistancePermissionsLoading,
  } = useHasPermissions([...EMPLOYEE_ASSISTANCE_ROUTE_PERMISSIONS]);

  const handleCreateClick = () => {
    void navigate({
      to: EmployeesNewRoute.to,
      search: (previous) => newEmployeeSearchSchema.parse(previous),
    });
  };

  const handleEmployeeEdit = (employeeId: string) => {
    void navigate({
      to: EmployeesEmployeeIdRoute.to,
      params: { employeeId },
      search: (previous) => employeeSearchSchema.parse(previous),
    });
  };

  const handleViewExecutions = (employeeId: string) => {
    void navigate({
      to: EmployeesEmployeeIdExecutionsRoute.to,
      params: { employeeId },
      search: (previous) => executionsSearchSchema.parse(previous),
    });
  };

  const handleViewAssistances = () => {
    void navigate({
      to: EmployeeAssistancesRoute.to,
      search: (previous) => assistancesSearchSchema.parse(previous),
    });
  };

  return (
    <div className="flex h-full flex-col">
      <Panel className="flex h-full min-h-0 flex-col">
        <PageHeader
          title={t('automations.aiAgents.index.title')}
          subtitle={t('automations.aiAgents.index.description')}
          icon={<AiRobotOutlineIcon className="h-8 w-8" />}
          actions={
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              {!assistancePermissionsLoading && canReadAssistances ? (
                <Button
                  onClick={handleViewAssistances}
                  variant="warning"
                  appearance="soft"
                  size="md"
                  className="w-full gap-2 sm:w-auto"
                >
                  <PiLifebuoy className="h-4 w-4" />
                  <Text size="small">
                    {t('automations.aiAgents.index.assistance')}
                  </Text>
                </Button>
              ) : null}
              <Button
                onClick={handleCreateClick}
                variant="primary"
                size="md"
                className="w-full gap-2 sm:w-auto"
              >
                <PiPlus className="h-4 w-4" />
                <Text size="small">
                  {t('automations.aiAgents.index.create')}
                </Text>
              </Button>
            </div>
          }
        />

        <div className="min-h-0 flex-1 overflow-auto bg-white p-4 sm:p-5">
          {loading ? (
            <EmployeesLoadingState />
          ) : error ? (
            <div className="flex h-full items-center justify-center">
              <ErrorState
                title={t('automations.aiAgents.index.errorTitle')}
                description={t('automations.aiAgents.index.errorDescription')}
                onAction={() => void refetch()}
                actionContent={t('automations.aiAgents.actions.retry')}
              />
            </div>
          ) : employees.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <EmptyState
                title={t('automations.aiAgents.index.emptyTitle')}
                description={t('automations.aiAgents.index.emptyDescription')}
                actionContent={t('automations.aiAgents.index.create')}
                onAction={handleCreateClick}
              />
            </div>
          ) : (
            <div className="mx-auto w-full max-w-5xl space-y-3">
              {employees.map(
                (employee: EmployeeListResponse['items'][number]) => (
                  <EmployeeCard
                    key={employee.employeeId}
                    employee={employee}
                    onEdit={() => handleEmployeeEdit(employee.employeeId)}
                    onViewExecutions={() =>
                      handleViewExecutions(employee.employeeId)
                    }
                  />
                )
              )}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
};
